import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  ListItem,
  Divider,
} from "@mui/material";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getSubscriptionDetailsByUserId } from "../services/api/subscription";
import { showToast } from "../store/slices/toastSlice";
import { PROFILE } from "../constant/MenuItem";
import {
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from "../styles/SidenavStyles";
import { User_Avatar } from "../assets/images";

const Header = () => {
  const dispatch = useDispatch();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname.startsWith(path);

  const pathnames = location.pathname.split("/").filter((x) => x);

  const [planDetails, setPlanDetails] = useState([]);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await getSubscriptionDetailsByUserId();
        if (response.responseCode === 200) {
          setPlanDetails(response.data);

          // Calculate the number of days left
          const endDate = new Date(response.data.end_date); // Assuming `end_date` is in a valid format
          const currentDate = new Date();
          const timeDifference = endDate - currentDate;

          // Calculate the number of days remaining
          const daysRemaining = Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert from milliseconds to days
          setDaysLeft(daysRemaining);
        }
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchSubscriptionDetails();
  }, []);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigation = (redirectTo) => {
    navigate(redirectTo);
    setAnchorElUser(null);
  };

  // Determine the color based on days left
  const getColor = (days) => {
    if (days < 15) return "red";
    if (days >= 15 && days < 30) return "grey";
    return "green"; // days >= 30
  };

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#FFFF",
          px: 3,
        }}
      >
        {/* Breadcrumbs on the left */}
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink
            component={Link}
            underline="hover"
            color="inherit"
            to="/dashboard"
          >
            Dashboard
          </MuiLink>

          {pathnames.map((value, index) => {
            const to = `/${pathnames.slice(0, index + 1).join("/")}`;
            const isLast = index === pathnames.length - 1;

            return isLast ? (
              <Typography color="text.primary" key={to}>
                {formatPathSegment(value)}
              </Typography>
            ) : (
              <MuiLink
                component={Link}
                underline="hover"
                color="inherit"
                to={to}
                key={to}
              >
                {formatPathSegment(value)}
              </MuiLink>
            );
          })}
        </Breadcrumbs>

        {/* Subscription details and user profile */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Subscription details */}
          {daysLeft !== null && (
            <Tooltip
              title={`Subscription ends on: ${new Date(
                planDetails.end_date
              ).toLocaleDateString("en-GB")}`}
              arrow
            >
              <Typography
                variant="h6"
                sx={{
                  mr: 3,
                  border: 1,
                  borderRadius: 1,
                  pl: 2,
                  pr: 2,
                  color: getColor(daysLeft), // Apply color based on daysLeft
                }}
              >
                {daysLeft} days left
              </Typography>
            </Tooltip>
          )}

          {/* Profile section */}
          <Box sx={{ flexGrow: 0, mr: 3 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt="Remy Sharp" src={User_Avatar} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {PROFILE.map((item, index) => {
                const active = isActive(item.redirectTo);
                const IconComponent = active
                  ? item.iconFilled
                  : item.iconOutlined;

                return (
                  <Tooltip
                    key={index}
                    title={!open ? item.title : ""}
                    placement="right"
                    arrow
                  >
                    <div>
                      <ListItem disablePadding sx={{ display: "block" }}>
                        <StyledListItemButton
                          active={active}
                          onClick={() => handleNavigation(item.redirectTo)}
                          sx={{
                            justifyContent: open ? "initial" : "center",
                          }}
                        >
                          <StyledListItemIcon
                            active={active}
                            sx={{ mr: open ? 3 : "auto" }}
                          >
                            <IconComponent />
                          </StyledListItemIcon>
                          <StyledListItemText
                            primary={item.title}
                            open={open}
                            active={active}
                          />
                        </StyledListItemButton>
                      </ListItem>

                      <Divider />
                    </div>
                  </Tooltip>
                );
              })}
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// Utility: Capitalize and format paths
const formatPathSegment = (segment) =>
  segment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export default Header;
